<div class="login-page">
  <form class="login-card" [formGroup]="loginForm" (ngSubmit)="onFormSubmit()">
    <!-- Brand -->
    <div class="brand">
      <picture>
        <source srcset="../../assets/logo_sm.jpg" media="(min-width: 300px) and (max-width: 576px)">
        <source srcset="../../assets/logo.jpg" media="(min-width: 576px)">
        <img src="../../assets/logo.jpg" alt="Labrador AI" class="brand-logo">
      </picture>
    </div>

    <h1 class="heading">Welcome back</h1>
    <p class="subheading" *ngIf="mode === 'password'">Sign in to your dashboard to keep things running.</p>
    <p class="subheading" *ngIf="mode === 'phone'">We'll text you a code — no password needed.</p>
    <p class="subheading" *ngIf="mode === 'otp'">Enter the 6-digit code we sent to your phone.</p>

    <!-- Work email -->
    <div class="field" *ngIf="mode === 'password'">
      <label for="username" class="field-label">Work email</label>
      <input id="username" type="text" formControlName="username" class="field-input"
        [ngClass]="{ 'is-invalid': submitted && f.username.errors }" placeholder="you@restaurant.com"
        autocomplete="username">
    </div>

    <!-- Password -->
    <div class="field" *ngIf="mode === 'password'">
      <div class="field-label-row">
        <label for="password" class="field-label">Password</label>
        <a [routerLink]="['/login/password-reset']" class="forgot-link">Forgot password?</a>
      </div>
      <div class="password-wrapper">
        <input id="password" type="{{hide ? 'password' : 'text'}}" formControlName="password" class="field-input"
          [ngClass]="{ 'is-invalid': submitted && f.password.errors }" placeholder="••••••••"
          autocomplete="current-password">
        <mat-icon class="eye-toggle" (click)="togglePasswordVisibility()">
          {{ hide ? 'visibility_off' : 'visibility' }}
        </mat-icon>
      </div>
    </div>

    <div *ngIf="error && mode === 'password'" class="alert">Invalid login credentials</div>

    <!-- Phone sign-in (ENG-5362) -->
    <ng-container [formGroup]="otpForm">
      <div class="field" *ngIf="mode === 'phone' || mode === 'otp'">
        <label for="phoneNumber" class="field-label">Mobile number</label>
        <div class="phone-input-wrapper" [class.is-invalid]="otpSubmitted && o.phoneNumber.errors"
          [class.readonly]="mode === 'otp'">
          <span class="country-code">+1</span>
          <input id="phoneNumber" type="tel" formControlName="phoneNumber" class="phone-input"
            [readonly]="mode === 'otp'" placeholder="(617) 555-0100"
            autocomplete="tel" inputmode="tel" maxlength="14"
            (input)="onPhoneInput($event)">
        </div>
        <span class="field-error"
          *ngIf="otpSubmitted && o.phoneNumber.errors?.usPhoneNumber">
          Enter a 10-digit US mobile number.
        </span>
      </div>

      <div class="field" *ngIf="mode === 'otp'">
        <label class="field-label">Verification code</label>
        <div class="otp-display" (click)="otpInput.focus()">
          <div *ngFor="let d of otpDigits; let i = index"
            class="otp-cell" [class.active]="i === otpCursor"
            [class.is-invalid]="otpSubmitted && o.otp.errors">
            {{ d }}
          </div>
        </div>
        <!-- Real input sits behind the visual boxes, captures all keystrokes -->
        <input #otpInput class="otp-real-input" type="text" formControlName="otp"
          inputmode="numeric" maxlength="6" autocomplete="one-time-code"
          (input)="onOtpRealInput()" (focus)="otpFocused = true"
          (blur)="otpFocused = false" (keyup)="updateOtpCursor()">
      </div>
    </ng-container>

    <div *ngIf="otpNotice && mode === 'otp'" class="notice">{{ otpNotice }}</div>
    <div *ngIf="otpError" class="alert">{{ otpError }}</div>

    <!-- Keep me signed in -->
    <label class="remember-me">
      <input type="checkbox" formControlName="rememberMe" class="remember-me-checkbox" />
      <span>Keep me signed in</span>
    </label>

    <!-- Sign in -->
    <button type="submit" class="btn-primary" [disabled]="loading" *ngIf="mode === 'password'">
      <span *ngIf="!loading">Sign in</span>
      <span *ngIf="!loading" class="arrow">&#8594;</span>
      <span *ngIf="loading">Signing in…</span>
    </button>

    <button type="submit" class="btn-primary" [disabled]="loading" *ngIf="mode === 'phone'">
      <span *ngIf="!loading">Send code</span>
      <span *ngIf="!loading" class="arrow">&#8594;</span>
      <span *ngIf="loading">Sending…</span>
    </button>

    <button type="submit" class="btn-primary" [disabled]="loading" *ngIf="mode === 'otp'">
      <span *ngIf="!loading">Verify and sign in</span>
      <span *ngIf="!loading" class="arrow">&#8594;</span>
      <span *ngIf="loading">Signing in…</span>
    </button>

    <!-- Resend / change number -->
    <div class="otp-actions" *ngIf="mode === 'otp'">
      <button type="button" class="link-button" (click)="requestOtp()"
        [disabled]="loading || resendSeconds > 0">
        {{ resendSeconds > 0 ? 'Resend code in ' + resendSeconds + 's' : 'Resend code' }}
      </button>
      <button type="button" class="link-button" (click)="usePhoneSignIn()" [disabled]="loading">
        Use a different number
      </button>
    </div>

    <!-- Divider -->
    <div class="divider"><span>OR</span></div>

    <!-- Phone / password toggle -->
    <button type="button" class="btn-google" (click)="usePhoneSignIn()" *ngIf="mode === 'password'">
      <mat-icon class="alt-signin-icon">smartphone</mat-icon>
      <span>Sign in with a text message</span>
    </button>

    <button type="button" class="btn-google" (click)="usePasswordSignIn()" *ngIf="mode !== 'password'">
      <mat-icon class="alt-signin-icon">lock_outline</mat-icon>
      <span>Sign in with email and password</span>
    </button>

    <!-- Google -->
    <button type="button" class="btn-google" (click)="onGoogleSignIn()" *ngIf="mode === 'password'">
      <svg class="google-icon" viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
        <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
        <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
        <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
        <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
      </svg>
      <span>Continue with Google</span>
    </button>

    <div *ngIf="googleError && mode === 'password'" class="alert">{{ googleError }}</div>

    <p class="signup-hint">Don't have an account yet? <a href="mailto:hello@labrador.ai">Talk to our team</a></p>

    <div class="login-footer">
      <span>&copy; 2026 Labrador AI, Inc.</span>
      <span class="footer-links">
        <a href="https://labrador.ai/privacy" target="_blank" rel="noopener">Privacy</a>
        <a href="https://labrador.ai/terms" target="_blank" rel="noopener">Terms</a>
        <a href="mailto:support@labrador.ai">Support</a>
      </span>
    </div>
  </form>
</div>
